import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, LogOut, User, X, Settings, UserCircle, ChevronDown, Shield, Database, HardDrive, Crown } from 'lucide-react';
import { googleAuthService } from '../../services/auth/googleAuthService';
import { useNavigate } from 'react-router-dom';
import UserRoleBadge from '../common/UserRoleBadge';
import { usePermissions } from '../../hooks/usePermissions';
import { isProUser } from '../../utils/proUtils';
import LoginModal from './LoginModal';

const GoogleSignInButton: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout, loginWithGoogle } = useAuth();
  const { isAdmin } = usePermissions();
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isPro = isProUser(user);

  const handleLoginClick = () => {
    // Always show modal, let the modal handle Google OAuth initialization
    setShowGoogleModal(true);
  };

  const handleNavigateToProfile = () => {
    setShowUserDropdown(false);
    navigate('/profile');
  };

  const handleNavigateToSettings = () => {
    setShowUserDropdown(false);
    navigate('/settings');
  };

  const handleCloseModal = () => {
    setShowGoogleModal(false);
  };


  // Auto close modal when user is authenticated
  useEffect(() => {
    if (isAuthenticated && showGoogleModal) {
      setShowGoogleModal(false);
    }
  }, [isAuthenticated, showGoogleModal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);



  if (isLoading) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 bg-white rounded-lg shadow-md">
        <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm text-gray-600">Đang tải...</span>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="relative" ref={dropdownRef}>
        {/* User Info Button - Only Avatar */}
        <button
          onClick={() => setShowUserDropdown(!showUserDropdown)}
          className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-white hover:border-blue-300 hover:scale-105 relative"
        >
          <span className="text-white font-bold text-lg drop-shadow-sm">
            {user.name?.charAt(0)?.toUpperCase() || 'U'}
          </span>
          {isPro && (
            <div className="absolute -bottom-1 -right-1 bg-yellow-400 rounded-full p-1 border-2 border-white shadow-md z-20 transform translate-x-1 translate-y-1">
              <Crown size={16} className="text-white fill-white" />
            </div>
          )}
        </button>

        {/* Dropdown Menu */}
        {showUserDropdown && (
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
            {/* User Info Header */}
            <div className="px-4 py-4 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg relative shrink-0">
                  <span className="text-white font-bold text-2xl drop-shadow-sm">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                  {isPro && (
                    <div className="absolute -bottom-1 -right-1 bg-yellow-400 rounded-full p-1.5 border-2 border-white shadow-md z-20 animate-bounce">
                      <Crown size={20} className="text-white fill-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-semibold text-blue-700 truncate">
                    {user.name}
                  </p>
                  <p className="text-sm text-gray-500 truncate" title={user.email}>
                    {user.email}
                  </p>
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-xs text-green-600 font-medium">
                      ✓ Đã xác thực Google
                    </p>
                    <UserRoleBadge />
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button
                onClick={handleNavigateToProfile}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <UserCircle className="w-5 h-5 text-blue-500" />
                <div className="text-left">
                  <span className="font-medium">Thông tin cá nhân</span>
                  <p className="text-xs text-gray-500">Xem và chỉnh sửa thông tin</p>
                </div>
              </button>

              <button
                onClick={handleNavigateToSettings}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings className="w-5 h-5 text-purple-500" />
                <div className="text-left">
                  <span className="font-medium">Cài đặt</span>
                  <p className="text-xs text-gray-500">Tùy chỉnh tài khoản và ứng dụng</p>
                </div>
              </button>

              {/* Admin Menu - Only show for admins */}
              {isAdmin && (
                <>
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      navigate('/admin');
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Shield className="w-5 h-5 text-red-500" />
                    <div className="text-left">
                      <span className="font-medium">Quản trị</span>
                      <p className="text-xs text-gray-500">Quản lý người dùng và hệ thống</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      navigate('/database-admin');
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Database className="w-5 h-5 text-blue-500" />
                    <div className="text-left">
                      <span className="font-medium">Database Admin</span>
                      <p className="text-xs text-gray-500">Quản lý cơ sở dữ liệu và backup</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      navigate('/sqlite-database');
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Database className="w-5 h-5 text-emerald-500" />
                    <div className="text-left">
                      <span className="font-medium">SQLite Database</span>
                      <p className="text-xs text-gray-500">Database SQL hiện đại nhất</p>
                    </div>
                  </button>
                </>
              )}
            </div>

            {/* Logout Button */}
            <div className="border-t border-gray-100 pt-1">
              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  logout();
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Đăng xuất</span>
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleLoginClick}
        className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:border-primary-500 hover:text-primary-600 transition-all duration-300 shadow-md hover:shadow-lg"
      >
        <LogIn className="w-4 h-4" />
        <span className="text-sm font-medium">Đăng nhập</span>
      </button>

      <LoginModal isOpen={showGoogleModal} onClose={handleCloseModal} />
    </>
  );
};

export default GoogleSignInButton;
